/**
 * Virtuelle / Software-Drucker — keine physische Kasse.
 * Logic lives in printerVirtualCore.cjs (shared with Electron main).
 */

// Shared CJS core (Electron: require same file via path).
// eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
const coreRaw = require('./printerVirtualCore.cjs');
// Some bundlers wrap CJS as `{ default: module.exports }`.
const core =
  coreRaw && typeof coreRaw.filterPhysicalPrinters === 'function'
    ? coreRaw
    : coreRaw?.default && typeof coreRaw.default.filterPhysicalPrinters === 'function'
      ? coreRaw.default
      : coreRaw || {};

export const VIRTUAL_PRINTER_NAME_RE = core.VIRTUAL_PRINTER_NAME_RE;
export const VIRTUAL_PORT_RE = core.VIRTUAL_PORT_RE;

export function isVirtualPrinterLabel(label) {
  if (typeof core.isVirtualPrinterLabel === 'function') {
    return core.isVirtualPrinterLabel(label);
  }
  return false;
}

export function isVirtualPrinterEntry(printer) {
  if (typeof core.isVirtualPrinterEntry === 'function') {
    return core.isVirtualPrinterEntry(printer);
  }
  // Fail open: treat as physical if core failed to load (better than crash).
  return !printer;
}

/**
 * Drop virtual/software queues (PDF, XPS, …). Only real devices remain.
 * @param {Array<object>|null|undefined} printers
 * @returns {Array<object>}
 */
export function filterPhysicalPrinters(printers) {
  if (typeof core.filterPhysicalPrinters === 'function') {
    return core.filterPhysicalPrinters(printers);
  }
  return Array.isArray(printers) ? printers.slice() : [];
}

/** Heuristic paper affinity from queue label / description (not OS capabilities). */
const THERMAL_NAME_RE =
  /\b(pos[-\s_]?(?:58|80)|58\s?mm|80\s?mm|thermal|receipt|ticket|esc\s*\/?\s*pos|tm-t\d+|tm-m\d+|tsp\d+|xprinter|rongta|rp\d{2,}|bixolon|citizen\s*ct|star\s*(?:tsp|mcp)|gprinter|zj-\d+|munbyn|kassen|bon-?drucker)\b/i;

const DOCUMENT_NAME_RE =
  /\b(laser|laserjet|inkjet|deskjet|officejet|pixma|ecotank|a4|a3|multifunction|mfp|scan|xerox|color\s*laser|pagewide)\b/i;

/**
 * @param {object|string|null} printer
 * @returns {'thermal'|'document'|'unknown'}
 */
export function classifyPrinterPaperAffinity(printer) {
  if (printer == null) return 'unknown';
  if (typeof printer === 'string') {
    const s = printer.trim();
    if (!s) return 'unknown';
    if (THERMAL_NAME_RE.test(s)) return 'thermal';
    if (DOCUMENT_NAME_RE.test(s)) return 'document';
    return 'unknown';
  }
  const hay = [printer.name, printer.deviceName, printer.displayName, printer.description]
    .filter(Boolean)
    .join(' ');
  if (!hay) return 'unknown';
  if (THERMAL_NAME_RE.test(hay)) return 'thermal';
  if (DOCUMENT_NAME_RE.test(hay)) return 'document';
  return 'unknown';
}

function pickFirstPreferred(list) {
  if (!Array.isArray(list) || list.length === 0) return null;
  return list.find((p) => p?.isDefault) || list[0];
}

/**
 * Prefer a physical queue for auto-discover.
 * Explicit saved name always wins (even thermal assigned to document).
 * Role-aware: document/label never auto-pick thermal queues.
 *
 * @param {Array<object>} [printers]
 * @param {string|null} [savedDeviceName]
 * @param {{ role?: string }} [options]
 */
export function pickPreferredPhysicalPrinter(
  printers = [],
  savedDeviceName = null,
  options = {}
) {
  const physical = filterPhysicalPrinters(printers);
  if (physical.length === 0) return null;

  if (savedDeviceName) {
    const saved = String(savedDeviceName).trim();
    const savedMatch = physical.find(
      (p) =>
        p.name === saved ||
        p.deviceName === saved ||
        String(p.name || '').toLowerCase() === saved.toLowerCase() ||
        String(p.deviceName || '').toLowerCase() === saved.toLowerCase()
    );
    if (savedMatch) return savedMatch;
  }

  const role = String(options.role || '').trim().toLowerCase();
  const scored = physical.map((p) => ({
    p,
    affinity: classifyPrinterPaperAffinity(p)
  }));

  if (role === 'document' || role === 'label') {
    // Never auto-route A4/labels onto POS thermal when unassigned.
    const nonThermal = scored.filter((x) => x.affinity !== 'thermal');
    const docs = nonThermal.filter((x) => x.affinity === 'document').map((x) => x.p);
    const unknown = nonThermal.filter((x) => x.affinity === 'unknown').map((x) => x.p);
    return pickFirstPreferred(docs) || pickFirstPreferred(unknown) || null;
  }

  // Receipt / default: thermal first, then unknown, then any physical.
  const thermal = scored.filter((x) => x.affinity === 'thermal').map((x) => x.p);
  const unknown = scored.filter((x) => x.affinity === 'unknown').map((x) => x.p);
  const document = scored.filter((x) => x.affinity === 'document').map((x) => x.p);
  return (
    pickFirstPreferred(thermal) ||
    pickFirstPreferred(unknown) ||
    pickFirstPreferred(document) ||
    null
  );
}
